import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loadFromStorage, saveToStorage, STORAGE_KEYS } from '../utils/localStorage';

const INITIAL_TASKS = [
  { id: 't1', title: 'Design system components', description: 'Create reusable UI components for the design system including buttons, inputs, and cards.', deadline: '2026-04-08', priority: 'High', category: 'Work', status: 'To Do', createdAt: '2026-04-01' },
  { id: 't2', title: 'Spring Boot authentication module', description: 'Complete the JWT-based auth flow with refresh tokens and role-based access.', deadline: '2026-04-15', priority: 'High', category: 'Study', status: 'In Progress', createdAt: '2026-04-02' },
  { id: 't3', title: 'Team meeting prep', description: 'Prepare agenda and slides for Monday\'s all-hands meeting.', deadline: '2026-04-12', priority: 'Medium', category: 'Work', status: 'To Do', createdAt: '2026-04-03' },
  { id: 't4', title: 'Read React 19 docs', description: 'Go through the new concurrent features and Server Components documentation.', deadline: '2026-04-20', priority: 'Low', category: 'Study', status: 'Completed', createdAt: '2026-04-01' },
  { id: 't5', title: 'Grocery shopping', description: 'Weekly groceries, cleaning supplies, and meal prep ingredients.', deadline: '2026-04-13', priority: 'Medium', category: 'Personal', status: 'Completed', createdAt: '2026-04-04' },
  { id: 't6', title: 'Fix OAuth login bug', description: 'Resolve the redirect loop issue on the production auth endpoint.', deadline: '2026-04-10', priority: 'High', category: 'Work', status: 'In Progress', createdAt: '2026-04-05' },
  { id: 't7', title: 'Write unit tests', description: 'Increase code coverage for the payment module to at least 85%.', deadline: '2026-04-18', priority: 'Medium', category: 'Work', status: 'To Do', createdAt: '2026-04-06' },
  { id: 't8', title: 'Portfolio website update', description: 'Add the three latest projects with live demos and case studies.', deadline: '2026-04-25', priority: 'Low', category: 'Personal', status: 'To Do', createdAt: '2026-04-07' },
];

const TaskContext = createContext(null);

export const useTasks = () => {
  const ctx = useContext(TaskContext);
  if (!ctx) throw new Error('useTasks must be used within TaskProvider');
  return ctx;
};

const genId = () => 't' + Date.now() + Math.random().toString(36).slice(2, 6);

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState(() => loadFromStorage(STORAGE_KEYS.TASKS, INITIAL_TASKS));
  const [dark, setDarkState] = useState(() => loadFromStorage(STORAGE_KEYS.THEME, true));
  const [settings, setSettingsState] = useState(() => loadFromStorage(STORAGE_KEYS.SETTINGS, { notifications: true, deadlineAlerts: true }));

  useEffect(() => { saveToStorage(STORAGE_KEYS.TASKS, tasks); }, [tasks]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.THEME, dark); }, [dark]);
  useEffect(() => { saveToStorage(STORAGE_KEYS.SETTINGS, settings); }, [settings]);

  const setDark = useCallback((val) => setDarkState(val), []);
  const updateSettings = useCallback((patch) => setSettingsState(s => ({ ...s, ...patch })), []);

  const addTask = useCallback((data) => {
    setTasks(t => [...t, { ...data, id: genId(), createdAt: new Date().toISOString().slice(0, 10) }]);
  }, []);

  const updateTask = useCallback((id, data) => {
    setTasks(t => t.map(x => x.id === id ? { ...x, ...data } : x));
  }, []);

  const deleteTask = useCallback((id) => {
    setTasks(t => t.filter(x => x.id !== id));
  }, []);

  const moveTask = useCallback((id, status) => {
    setTasks(t => t.map(x => x.id === id ? { ...x, status } : x));
  }, []);

  const reorderTasks = useCallback((newTasks) => setTasks(newTasks), []);

  const resetTasks = useCallback(() => setTasks(INITIAL_TASKS), []);
  const clearAllTasks = useCallback(() => setTasks([]), []);

  const getDeadlineStatus = useCallback((deadline, status) => {
    if (status === 'Completed' || !deadline) return 'normal';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(deadline); d.setHours(0, 0, 0, 0);
    const diff = (d - today) / (1000 * 60 * 60 * 24);
    if (diff < 0) return 'overdue';
    if (diff <= 2) return 'near';
    return 'normal';
  }, []);

  const alertTasks = tasks.filter(t => {
    const ds = getDeadlineStatus(t.deadline, t.status);
    return ds === 'overdue' || ds === 'near';
  });

  return (
    <TaskContext.Provider value={{
      tasks, dark, setDark, settings, updateSettings,
      addTask, updateTask, deleteTask, moveTask, reorderTasks, resetTasks, clearAllTasks,
      getDeadlineStatus, alertTasks,
    }}>
      {children}
    </TaskContext.Provider>
  );
};
